import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";
import { deleteTaskWithUndo, bulkDeleteTasksWithUndo } from "@/lib/deleteWithUndo";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { statusBadgeClasses, statusLabel, statusDotClass, formatDate } from "@/lib/statusColor";
import { toast } from "sonner";
import {
  Plus,
  Calendar,
  Mail,
  MessageSquare,
  ListChecks,
  CircleCheck,
  Clock3,
  AlertTriangle,
  Copy,
  Pencil,
  CheckCircle2,
  RotateCcw,
  Search,
  X,
  FileBarChart,
  Trash2,
  ListTodo,
  Route as RouteIcon,
} from "lucide-react";

const FILTERS = {
  all: { label: "Total", accent: "bg-slate-900 text-white", icon: <ListChecks size={16} /> },
  green: { label: "On Track", accent: "bg-emerald-100 text-emerald-800", icon: <Clock3 size={16} /> },
  yellow: { label: "Past Deadline", accent: "bg-amber-100 text-amber-800", icon: <Clock3 size={16} /> },
  red: { label: "Overdue", accent: "bg-rose-100 text-rose-800", icon: <AlertTriangle size={16} /> },
  completed: { label: "Completed", accent: "bg-slate-100 text-slate-800", icon: <CircleCheck size={16} /> },
};

const COLUMN_PALETTES = [
  { header: "bg-gradient-to-br from-indigo-500 to-violet-600", ring: "ring-indigo-200", avatar: "bg-white text-indigo-700" },
  { header: "bg-gradient-to-br from-emerald-500 to-teal-600", ring: "ring-emerald-200", avatar: "bg-white text-emerald-700" },
  { header: "bg-gradient-to-br from-amber-500 to-orange-500", ring: "ring-amber-200", avatar: "bg-white text-amber-700" },
  { header: "bg-gradient-to-br from-fuchsia-500 to-pink-500", ring: "ring-fuchsia-200", avatar: "bg-white text-fuchsia-700" },
  { header: "bg-gradient-to-br from-sky-500 to-cyan-500", ring: "ring-sky-200", avatar: "bg-white text-sky-700" },
  { header: "bg-gradient-to-br from-rose-500 to-red-500", ring: "ring-rose-200", avatar: "bg-white text-rose-700" },
];

function paletteFor(email) {
  let h = 0;
  for (let i = 0; i < email.length; i++) h = (h * 31 + email.charCodeAt(i)) >>> 0;
  return COLUMN_PALETTES[h % COLUMN_PALETTES.length];
}

function dateKey(iso) {
  return new Date(iso).toISOString().slice(0, 10);
}

function friendlyDate(iso) {
  const d = new Date(iso);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  const dOnly = new Date(d); dOnly.setHours(0, 0, 0, 0);
  if (dOnly.getTime() === today.getTime()) return "Today";
  if (dOnly.getTime() === tomorrow.getTime()) return "Tomorrow";
  if (dOnly.getTime() === yesterday.getTime()) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

function statusCardTint(color) {
  switch (color) {
    case "green": return "border-l-emerald-500 bg-emerald-50/40";
    case "yellow": return "border-l-amber-500 bg-amber-50/40";
    case "red": return "border-l-rose-500 bg-rose-50/40";
    case "completed": return "border-l-slate-400 bg-slate-50/60";
    default: return "border-l-slate-400 bg-white";
  }
}

export default function Dashboard() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [view, setView] = useState("active");  // "active" | "completed"
  const [selected, setSelected] = useState(() => new Set());  // task ids selected in Completed tab
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/tasks");
      setTasks(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const stats = useMemo(() => ({
    all: tasks.length,
    green: tasks.filter((t) => t.color === "green" && t.status !== "completed").length,
    yellow: tasks.filter((t) => t.color === "yellow" && t.status !== "completed").length,
    red: tasks.filter((t) => t.color === "red" && t.status !== "completed").length,
    completed: tasks.filter((t) => t.status === "completed").length,
    unread: tasks.filter((t) => t.unread).length,
  }), [tasks]);

  const q = query.trim().toLowerCase();
  const matchesQuery = (t) => !q || (
    (t.title || "").toLowerCase().includes(q) ||
    (t.description || "").toLowerCase().includes(q) ||
    (t.assignee_name || "").toLowerCase().includes(q) ||
    (t.assignee_email || "").toLowerCase().includes(q)
  );
  const matchesFilter = (t) => {
    if (filter === "all") return true;
    if (filter === "completed") return t.status === "completed";
    return t.color === filter && t.status !== "completed";
  };

  const columns = useMemo(() => {
    const map = new Map();
    for (const t of tasks) {
      // ACTIVE view hides completed tasks entirely (they live on the Completed tab)
      const isCompleted = t.status === "completed";
      if (view === "active" && isCompleted) continue;
      if (view === "completed" && !isCompleted) continue;
      if (!matchesFilter(t) || !matchesQuery(t)) continue;
      const key = t.assignee_email;
      if (!map.has(key)) {
        map.set(key, {
          name: t.assignee_name, email: t.assignee_email,
          tasks: [], total: 0, green: 0, yellow: 0, red: 0, completed: 0, unread: 0,
        });
      }
      const a = map.get(key);
      a.tasks.push(t);
      a.total += 1;
      if (t.unread) a.unread += 1;
      if (t.status === "completed") a.completed += 1;
      else if (t.color === "green") a.green += 1;
      else if (t.color === "yellow") a.yellow += 1;
      else if (t.color === "red") a.red += 1;
    }
    for (const a of map.values()) {
      a.tasks.sort((x, y) => new Date(x.deadline) - new Date(y.deadline));
    }
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
     
  }, [tasks, filter, query, view]);

  const toggleComplete = async (task) => {
    const next = task.status === "completed" ? "in_progress" : "completed";
    try {
      const { data } = await api.patch(`/tasks/${task.id}`, { status: next });
      setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, ...data } : t)));
      toast.success(next === "completed" ? "Marked completed — moved to Completed tab" : "Reopened");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteTask = (task) => {
    deleteTaskWithUndo({
      task,
      onHide: (arr) => setTasks((prev) => prev.filter((x) => !arr.find((y) => y.id === x.id))),
      onRestore: (arr) => setTasks((prev) => [...prev, ...arr]),
    });
  };

  const toggleSelect = (taskId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId); else next.add(taskId);
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const completedTaskIds = useMemo(
    () => tasks.filter((t) => t.status === "completed").map((t) => t.id),
    [tasks]
  );
  const allSelectedInView = selected.size > 0 && completedTaskIds.every((id) => selected.has(id));
  const toggleSelectAll = () => {
    if (allSelectedInView) {
      setSelected(new Set());
    } else {
      setSelected(new Set(completedTaskIds));
    }
  };

  const bulkDelete = () => {
    const chosen = tasks.filter((t) => selected.has(t.id));
    if (chosen.length === 0) return;
    if (chosen.length >= 10) {
      const ok = window.confirm(
        `You're about to delete ${chosen.length} tasks. They'll move to the Trash bin for 24 hours before being permanently removed. Continue?`
      );
      if (!ok) return;
    }
    setSelected(new Set());
    bulkDeleteTasksWithUndo({
      tasks: chosen,
      onHide: (arr) => setTasks((prev) => prev.filter((x) => !arr.find((y) => y.id === x.id))),
      onRestore: (arr) => setTasks((prev) => [...prev, ...arr]),
    });
  };

  const copyShare = async (task) => {
    const url = `${window.location.origin}/board/${task.share_token}`;
    try { await navigator.clipboard.writeText(url); toast.success("Share link copied"); }
    catch { toast.error("Could not copy"); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-6 py-8">
        <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
          <div>
            <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
              Master dashboard
            </h1>
            <p className="text-slate-500 mt-2">Every assignee, every task, in one place.</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/report">
              <Button variant="outline" data-testid="view-report-btn">
                <FileBarChart size={16} className="mr-1.5" /> View Report
              </Button>
            </Link>
            <Link to="/create">
              <Button data-testid="create-task-btn">
                <Plus size={16} className="mr-1.5" /> New Task
              </Button>
            </Link>
          </div>
        </div>

        <div className="relative mb-5">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by title, description, or assignee…"
            className="pl-9 pr-10 h-11 bg-white" data-testid="dashboard-search-input" />
          {query && (
            <button type="button" onClick={() => setQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100 text-slate-500"
              data-testid="dashboard-search-clear" aria-label="Clear search">
              <X size={14} />
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          <MetricCard testId="stat-all" filterKey="all" icon={FILTERS.all.icon}
            label={stats.unread > 0 ? `Total · ${stats.unread} new` : FILTERS.all.label}
            value={stats.all} accent={FILTERS.all.accent} active={filter === "all"}
            onClick={() => setFilter("all")} />
          {Object.entries(FILTERS).filter(([k]) => k !== "all").map(([key, f]) => (
            <MetricCard key={key} testId={`stat-${key}`} filterKey={key} icon={f.icon} label={f.label}
              value={stats[key]} accent={f.accent} active={filter === key}
              onClick={() => setFilter((cur) => (cur === key ? "all" : key))} />
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
          {/* Left sidebar — coloured nav tabs */}
          <aside className="space-y-3" data-testid="dashboard-sidebar">
            <Link to="/todo" className="block group" data-testid="sidebar-todo-link">
              <div className="rounded-lg p-4 text-white shadow-sm bg-gradient-to-br from-indigo-500 to-violet-600 group-hover:shadow-md transition-shadow">
                <div className="flex items-center gap-2">
                  <ListTodo size={18} />
                  <span className="font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>My To-Do</span>
                </div>
                <p className="text-[11px] opacity-90 mt-1">Personal list for your own notes and reminders.</p>
              </div>
            </Link>
            <Link to="/routes" className="block group" data-testid="sidebar-routes-link">
              <div className="rounded-lg p-4 text-white shadow-sm bg-gradient-to-br from-emerald-500 to-teal-600 group-hover:shadow-md transition-shadow">
                <div className="flex items-center gap-2">
                  <RouteIcon size={18} />
                  <span className="font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>Routes</span>
                </div>
                <p className="text-[11px] opacity-90 mt-1">Split addresses across salespeople and send map links.</p>
              </div>
            </Link>
          </aside>

          {/* Main content */}
          <div>
            {/* View tabs: Active | Completed */}
            <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
              <div className="inline-flex rounded-md border border-slate-200 bg-white p-1" data-testid="view-tabs">
                <button type="button"
                  onClick={() => { setView("active"); clearSelection(); }}
                  data-testid="view-tab-active"
                  className={`px-4 py-1.5 text-sm rounded ${view === "active" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}>
                  Active
                </button>
                <button type="button"
                  onClick={() => setView("completed")}
                  data-testid="view-tab-completed"
                  className={`px-4 py-1.5 text-sm rounded ${view === "completed" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}>
                  Completed ({stats.completed})
                </button>
              </div>
              {view === "completed" && completedTaskIds.length > 0 && (
                <div className="flex items-center gap-2" data-testid="bulk-toolbar">
                  <button type="button" onClick={toggleSelectAll} data-testid="select-all-btn"
                    className="text-xs text-slate-600 hover:text-slate-900 underline underline-offset-2">
                    {allSelectedInView ? "Clear selection" : "Select all"}
                  </button>
                  {selected.size > 0 && (
                    <Button size="sm" variant="outline" onClick={bulkDelete} data-testid="bulk-delete-btn"
                      className="text-rose-700 hover:text-rose-800 border-rose-200 hover:bg-rose-50">
                      <Trash2 size={12} className="mr-1.5" /> Delete {selected.size} selected
                    </Button>
                  )}
                </div>
              )}
            </div>

            {loading ? (
              <p className="text-slate-500">Loading…</p>
            ) : tasks.length === 0 ? (
              <EmptyState />
            ) : columns.length === 0 ? (
              <Card className="p-8 rounded-md border-slate-200 shadow-sm text-center" data-testid="no-results">
                <p className="text-slate-500">
                  {view === "completed" ? "No completed tasks yet." : "No tasks match this filter."}
                </p>
              </Card>
            ) : (
              <div className="overflow-x-auto pb-4 -mx-2 px-2" data-testid="columns-scroll">
                <div className="flex gap-4 min-w-min">
                  {columns.map((a) => (
                    <AssigneeColumn
                      key={a.email}
                      assignee={a}
                      view={view}
                      selected={selected}
                      onToggleSelect={toggleSelect}
                      onToggle={toggleComplete}
                      onCopy={copyShare}
                      onOpen={(id) => navigate(`/tasks/${id}`)}
                      onDelete={deleteTask}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function MetricCard({ label, value, icon, accent, active, onClick, testId, filterKey }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId} aria-pressed={active}
      className={`text-left p-4 rounded-md border shadow-sm bg-white hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-2 transition-colors ${active ? "border-slate-900 ring-1 ring-slate-900" : "border-slate-200"}`}>
      <div className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md ${accent}`}>{icon}{label}</div>
      <div className="mt-2 text-3xl font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{value}</div>
      <div className={`text-[11px] mt-1 ${active ? "text-slate-900" : "text-slate-400"}`}>
        {active && filterKey !== "all" ? "Filter active — click to clear" : "Click to filter"}
      </div>
    </button>
  );
}

function AssigneeColumn({ assignee, view, selected, onToggleSelect, onToggle, onCopy, onOpen, onDelete }) {
  const pal = paletteFor(assignee.email);
  const donePct = assignee.total === 0 ? 0 : Math.round((assignee.completed / assignee.total) * 100);
  // group tasks by date key
  const groups = [];
  const seen = new Map();
  for (const t of assignee.tasks) {
    const k = dateKey(t.deadline);
    if (!seen.has(k)) { seen.set(k, { key: k, iso: t.deadline, tasks: [] }); groups.push(seen.get(k)); }
    seen.get(k).tasks.push(t);
  }

  return (
    <div className={`shrink-0 w-[320px] rounded-lg border border-slate-200 bg-white shadow-sm ring-2 ${pal.ring}`} data-testid={`col-${assignee.email}`}>
      <div className={`rounded-t-lg p-4 text-white ${pal.header}`}>
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-full grid place-items-center shrink-0 text-sm font-semibold ${pal.avatar}`}>
            {assignee.name.slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-base font-semibold truncate" style={{ fontFamily: "Outfit, sans-serif" }}>{assignee.name}</div>
              {assignee.unread > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-white/25 backdrop-blur-sm border border-white/30 inline-flex items-center gap-1" data-testid={`col-unread-${assignee.email}`}>
                  <span className="w-1 h-1 rounded-full bg-white animate-pulse" />{assignee.unread} new
                </span>
              )}
            </div>
            <div className="text-[11px] opacity-90 truncate flex items-center gap-1 mt-0.5">
              <Mail size={10} /> {assignee.email}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-2xl font-semibold leading-none" style={{ fontFamily: "Outfit, sans-serif" }}>{assignee.total}</div>
            <div className="text-[10px] opacity-90">tasks</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-1 mt-3">
          {assignee.green > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/90 text-white">{assignee.green} on track</span>}
          {assignee.yellow > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/90 text-white">{assignee.yellow} past</span>}
          {assignee.red > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-500/90 text-white">{assignee.red} overdue</span>}
          {assignee.completed > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/25 text-white">{assignee.completed} done</span>}
        </div>
        <div className="flex items-center gap-2 mt-3">
          <Progress value={donePct} className="h-1 flex-1 bg-white/20 [&>div]:bg-white" />
          <span className="text-[11px] tabular-nums opacity-90">{donePct}%</span>
        </div>
      </div>

      <div className="p-3 space-y-4 max-h-[600px] overflow-y-auto">
        {groups.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-6">No tasks in view.</p>
        ) : groups.map((g) => (
          <div key={g.key}>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-1.5">
              <Calendar size={11} /> {friendlyDate(g.iso)}
              <span className="text-slate-400 font-normal">({g.tasks.length})</span>
            </div>
            <div className="space-y-2">
              {g.tasks.map((t) => (
                <TaskMiniCard key={t.id} task={t} view={view}
                  isSelected={selected && selected.has(t.id)}
                  onToggleSelect={onToggleSelect}
                  onToggle={onToggle} onCopy={onCopy} onOpen={onOpen} onDelete={onDelete} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TaskMiniCard({ task, view, isSelected, onToggleSelect, onToggle, onCopy, onOpen, onDelete }) {
  const done = task.status === "completed";
  return (
    <div className={`p-3 rounded-md border border-slate-200 border-l-[3px] shadow-sm bg-white ${statusCardTint(task.color)} ${task.unread ? "ring-1 ring-blue-200" : ""} ${isSelected ? "ring-2 ring-rose-300" : ""}`} data-testid={`mini-task-${task.id}`}>
      <div className="flex items-start justify-between gap-2">
        {view === "completed" && onToggleSelect && (
          <input
            type="checkbox"
            checked={!!isSelected}
            onChange={() => onToggleSelect(task.id)}
            className="mt-1 h-4 w-4 accent-rose-500 cursor-pointer"
            aria-label={`Select ${task.title}`}
            data-testid={`select-task-${task.id}`}
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={`w-2 h-2 rounded-full shrink-0 ${statusDotClass(task.color)}`} />
            <button type="button" onClick={() => onOpen(task.id)}
              className={`text-left text-sm font-semibold text-slate-900 hover:underline ${done ? "line-through opacity-60" : ""}`}
              data-testid={`mini-task-title-${task.id}`}>
              {task.title}
            </button>
            {task.unread && (
              <span className="text-[9px] px-1 py-0.5 rounded bg-blue-600 text-white uppercase tracking-wider" data-testid={`mini-unread-${task.id}`}>New</span>
            )}
            {task.parent_task_id && (
              <span className="text-[9px] px-1 py-0.5 rounded border border-slate-300 text-slate-600 uppercase tracking-wider">Sub</span>
            )}
          </div>
          {task.description && (
            <p className="text-xs text-slate-600 mt-1 line-clamp-2 leading-snug">{task.description}</p>
          )}
          <div className="flex items-center gap-2 mt-2 text-[10px] text-slate-500">
            <span>{formatDate(task.deadline).split(",")[0]}</span>
            <Badge variant="outline" className={`border ${statusBadgeClasses(task.color)} px-1.5 py-0 h-4 text-[9px]`}>
              {statusLabel(task.color, task.status)}
            </Badge>
            {task.comment_count > 0 && (
              <span className="inline-flex items-center gap-0.5"><MessageSquare size={9} /> {task.comment_count}</span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-0.5 mt-2 -mr-1">
        <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => onToggle(task)} title={done ? "Reopen" : "Complete"} data-testid={`mini-toggle-${task.id}`}>
          {done ? <RotateCcw size={12} /> : <CheckCircle2 size={12} />}
        </Button>
        <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => onCopy(task)} title="Copy link" data-testid={`mini-copy-${task.id}`}>
          <Copy size={12} />
        </Button>
        <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => onOpen(task.id)} title="Open" data-testid={`mini-open-${task.id}`}>
          <Pencil size={12} />
        </Button>
        {view === "completed" && onDelete && (
          <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-rose-600 hover:text-rose-800" onClick={() => onDelete(task)} title="Delete" data-testid={`mini-delete-${task.id}`}>
            <Trash2 size={12} />
          </Button>
        )}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <Card className="p-10 rounded-md border-slate-200 shadow-sm text-center" data-testid="empty-state">
      <img
        src="https://images.unsplash.com/photo-1533090161767-e6ffed986c88?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxtaW5pbWFsJTIwd29ya3NwYWNlJTIwZGVzayUyMGFic3RyYWN0fGVufDB8fHx8MTc4NDQ3NDI5OXww&ixlib=rb-4.1.0&q=85"
        alt="" className="w-40 h-40 object-cover rounded-md mx-auto mb-6" />
      <h3 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>No tasks yet</h3>
      <p className="text-slate-500 mt-2 max-w-md mx-auto">Create your first task, assign it to someone, and share the link.</p>
      <Link to="/create" className="inline-block mt-5">
        <Button data-testid="empty-create-task-btn"><Plus size={16} className="mr-1.5" /> New Task</Button>
      </Link>
    </Card>
  );
}
